import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

// Track last error to prevent sending the same error twice in a session
let lastErrorFingerprint: string | null = null;

Sentry.init({
  dsn: 'https://fbd5eff1839e00636c4aea5742234fd4@o4511110644760576.ingest.de.sentry.io/4511664919806032',
  environment: environment.production ? 'production' : 'development',
  release: 'docucenter-frontend@0.0.0', // Update this with actual version from package.json or CI/CD
  integrations: [
    Sentry.browserTracingIntegration(),
    // Sentry.replayIntegration({
    //   //maskAllText: false, // This could send sensitive data to Sentry. Cookies, passwords, and other sensitive data could be captured.
    //   blockAllMedia: false,
    // }),
    Sentry.rewriteFramesIntegration({
      // Normalize file paths for source map matching
      iteratee: (frame) => {
        if (frame.filename) {
          // Remove query parameters and hashes
          frame.filename = frame.filename.split('?')[0].split('#')[0];

          // For localhost URLs, extract just the filename
          if (frame.filename.includes('localhost')) {
            const match = frame.filename.match(/\/([^/]+\.js)$/);
            if (match) {
              frame.filename = `~/${match[1]}`;
            }
          }
          // For production builds, ensure proper prefix
          else if (!frame.filename.startsWith('~/')) {
            const cleanPath = frame.filename.replace(/^\/+/, '');
            frame.filename = `~/${cleanPath}`;
          }
        }
        return frame;
      },
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: undefined,
  // Session Replay
  // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  dataCollection: {
    // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#dataCollection
    // userInfo: false,
    // httpBodies: []
  },

  // Filter out known non-critical errors
  beforeSend(event, hint) {
    const error = hint.originalException;

    if (error && typeof error === 'object' && 'message' in error) {
      const errorMessage = String(error.message);

      // Ignore NG0100 errors with undefined values. This is a known issue
      //  with the styleguide library where classes are created before full initialization
      if (errorMessage.includes('NG0100') && errorMessage.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
        return null; // Drop this event
      }

      // Ignore dynamic module loading errors in localhost (development environment)
      // These occur when using incorrect keycloak environment configuration locally.
      // Production chunk-load failures (same message, no 'localhost') are handled by the
      // ChunkLoadAwareErrorHandler (app.config.ts), which reloads the page to recover.
      if (errorMessage.includes('error loading dynamically imported module') && errorMessage.includes('localhost')) {
        return null; // Drop this event
      }
    }

    // Deduplicate identical errors - never send the same error twice in a session
    const exceptionValue = event.exception?.values?.[0];
    const errorFingerprint = exceptionValue
      ? `${exceptionValue.value || ''}|${exceptionValue.type || ''}|${
          exceptionValue.stacktrace?.frames?.[0]?.filename || ''
        }`
      : null;

    if (errorFingerprint && errorFingerprint === lastErrorFingerprint) {
      // Same error as last one - drop it
      return null;
    }

    // Update last error tracking
    lastErrorFingerprint = errorFingerprint;

    return event;
  },
});

bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
